import { ClarionButton } from '@clarion/ClarionButton';
import { ClarionCallout } from '@clarion/ClarionCallout';
import { ClarionDialog, ClarionDialogFooter } from '@clarion/ClarionDialog';
import { ClarionTextArea } from '@clarion/ClarionTextArea';
import { ClarionTextField } from '@clarion/ClarionTextField';
import { useState } from 'react';
import { useUserState } from '../auth/UserState';
import { Assignment, TimeEntry } from './assignment';
import { addManualTimeEntry, editTimeEntry } from './assignmentService';

type TimeEntryFormProps = {
  isOpen: boolean;
  onClose: () => void;
  assignment: Assignment;
  existingEntry?: TimeEntry;
  onEntryAdded: (updatedAssignment: Assignment) => void;
};

// Helper function to format date for datetime-local input (includes seconds
// so entries that differ only by seconds aren't collapsed to the same minute)
const formatDateForInput = (date: Date): string => {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const seconds = date.getSeconds().toString().padStart(2, '0');

  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
};

export default function TimeEntryForm({
  isOpen,
  onClose,
  assignment,
  existingEntry,
  onEntryAdded,
}: TimeEntryFormProps) {
  const { user } = useUserState();
  const [startDateTime, setStartDateTime] = useState<string>(
    existingEntry?.startTime
      ? formatDateForInput(new Date(existingEntry.startTime))
      : formatDateForInput(new Date()),
  );
  const [endDateTime, setEndDateTime] = useState<string>(
    existingEntry?.endTime
      ? formatDateForInput(new Date(existingEntry.endTime))
      : formatDateForInput(new Date()),
  );
  const [notes, setNotes] = useState(existingEntry?.notes || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [datetimeFieldErrors, setDatetimeFieldErrors] = useState<{
    start?: string;
    end?: string;
  }>({});

  const isEditMode = Boolean(existingEntry);

  const handleSubmit = async () => {
    if (!user) return;

    try {
      setIsSubmitting(true);
      setError(null);
      setDatetimeFieldErrors({});

      const startTime = new Date(startDateTime).getTime();
      const endTime = new Date(endDateTime).getTime();

      const dtErrors: { start?: string; end?: string } = {};
      if (Number.isNaN(startTime)) dtErrors.start = 'Enter a valid start date and time.';
      if (Number.isNaN(endTime)) dtErrors.end = 'Enter a valid end date and time.';
      if (Object.keys(dtErrors).length > 0) {
        setDatetimeFieldErrors(dtErrors);
        return;
      }

      if (startTime >= endTime) {
        setDatetimeFieldErrors({
          end: 'End time must be after start time.',
        });
        return;
      }

      let updatedAssignment: Assignment;

      if (isEditMode && existingEntry) {
        // Update existing entry
        updatedAssignment = await editTimeEntry(
          assignment.id,
          assignment.workspaceId,
          existingEntry.id,
          user.uid,
          user.displayName || user.email || 'Unknown User',
          {
            startTime,
            endTime,
            notes,
            isManualEntry: true,
          },
        );
      } else {
        // Add new time entry
        updatedAssignment = await addManualTimeEntry(
          assignment.id,
          assignment.workspaceId,
          user.uid,
          user.displayName || user.email || 'Unknown User',
          startTime,
          endTime,
          notes,
        );
      }

      onEntryAdded(updatedAssignment);
      onClose();
    } catch (error) {
      console.error('Error saving time entry:', error);
      setError('Failed to save time entry. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ClarionDialog
      isOpen={isOpen}
      onClose={onClose}
      title={isEditMode ? 'Edit time entry' : 'Add time entry'}
      className="flex flex-col gap-2 min-w-xs"
    >
      {error && (
        <ClarionCallout intent="danger" title="Something went wrong" className="mb-4">
          {error}
        </ClarionCallout>
      )}

      <ClarionTextField
        label="Start Date/Time"
        type="datetime-local"
        value={startDateTime}
        onChange={(value) => {
          setStartDateTime(value);
          setDatetimeFieldErrors((e) => ({ ...e, start: undefined }));
        }}
        fill
        isInvalid={!!datetimeFieldErrors.start}
        errorMessage={datetimeFieldErrors.start}
      />

      <ClarionTextField
        label="End Date/Time"
        type="datetime-local"
        value={endDateTime}
        onChange={(value) => {
          setEndDateTime(value);
          setDatetimeFieldErrors((e) => ({ ...e, end: undefined }));
        }}
        fill
        isInvalid={!!datetimeFieldErrors.end}
        errorMessage={datetimeFieldErrors.end}
      />

      <ClarionTextArea
        label="Notes"
        value={notes}
        onChange={setNotes}
        placeholder="Add notes about this time entry"
        fill
        autoResize
        style={{ minHeight: 80 }}
        className="-mb-6"
      />

      <ClarionDialogFooter>
        <ClarionButton onClick={onClose} isDisabled={isSubmitting}>
          Cancel
        </ClarionButton>
        <ClarionButton
          intent="primary"
          onClick={handleSubmit}
          loading={isSubmitting}
          isDisabled={isSubmitting}
        >
          {isEditMode ? 'Update' : 'Add'} Time Entry
        </ClarionButton>
      </ClarionDialogFooter>
    </ClarionDialog>
  );
}
